import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { CapsuleButton } from "../../../Everou/_components";
import { LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";

/**
 * The "Nombre" field shared by every PMS template form.
 *
 * A template was only identifiable by company + credentials, which is
 * unreadable as soon as the manager has two accounts of one PMS -- and unusable
 * in the picker that moves an integration from one template to another
 * (BAC-520). The name is optional and purely descriptive.
 *
 * On an existing template it saves ITSELF, through the rename endpoint, instead
 * of riding along in the form's Save: that save runs the backend's per-company
 * branch, which re-validates the credentials against the PMS and, for Enso
 * Connect, mints a new apikey and rewrites it onto every integration. Nobody
 * expects renaming a label to touch a live webhook. On a template that does not
 * exist yet there is nothing to rename, so `templateName` travels inside the
 * create like any other field.
 *
 * `standaloneSave: false` drops that second button for the companies whose save
 * validates nothing and mints nothing (Manual): there, two Save buttons on one
 * short form only raise the question of which one does what.
 */
export function useIntegrationName(
    integration: LocalEditableIntegration,
    // Where the name is NOT optional, because nothing else identifies the
    // template (Manual holds no credentials at all). Marks the field so the
    // manager knows before the Save sits there disabled.
    { required = false, standaloneSave = true } = {},
) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [templateName, setTemplateName] = useState(integration.name ?? "");
    // What is stored for this template as far as this form knows. Kept locally
    // because the rename does NOT re-list the integrations: re-listing rebuilds
    // every row, and each form resets its fields from the new instance, so a
    // manager who edited the apikey and the name and saved the name would lose
    // the apikey they typed. The store catches up on its next natural refresh.
    const [savedName, setSavedName] = useState(integration.name ?? "");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setTemplateName(integration.name ?? "");
        setSavedName(integration.name ?? "");
        setErrorMessage(null);
    }, [integration]);

    const nameChanged = templateName !== savedName;
    const canRename = !!integration.uid && nameChanged && !isSaving;

    async function onRename() {
        setErrorMessage(null);
        setIsSaving(true);
        try {
            await dispatch(PMSIntegrationsActions.renameIntegration(
                integration.uid,
                templateName,
            ));
            setSavedName(templateName);
        } catch (error: any) {
            // ORGANIZATIONS_NOT_LOADED and friends are internal: the user gets
            // the generic message, not our identifier.
            setErrorMessage(
                error.message?.includes(" ") ? error.message : t("global_error_network"));
        } finally {
            setIsSaving(false);
        }
    }

    const nameInput = (
        <div className="col-span-full">
            <label className="font-weight-bold mb-1 d-block">
                {t("mod_integrations_template_name")}
                <span className="text-muted font-weight-normal">
                    {` (${t(required ? "global_required" : "global_optional")})`}
                </span>
            </label>
            <div className="d-flex align-items-center">
                <input
                    className={`${!integration.editable ? "cna" : ""} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    maxLength={64}
                    readOnly={!integration.editable}
                    value={templateName}
                    placeholder={t("mod_integrations_template_name_placeholder")}
                    onChange={e => setTemplateName(e.target.value)}
                />
                {standaloneSave && !!integration.uid && (
                    <CapsuleButton
                        size="SMALL"
                        classNameExtra="ml-2 flex-shrink-0"
                        isEnabled={canRename}
                        onClick={onRename}
                        text={t("global_save")}
                    />
                )}
            </div>
            {errorMessage && (
                <div className="small text-danger mt-1">{errorMessage}</div>
            )}
            {integration.owner && (
                // A customer's account, reached through support (BAC-673):
                // say whose key the Save below is about to rotate.
                <div className="small text-warning mt-1" data-testid="integration-owner">
                    {t(integration.owner.type === "organization"
                        ? "mod_pms_account_owner_organization"
                        : "mod_pms_account_owner_user",
                    { name: integration.owner.name })}
                </div>
            )}
        </div>
    );

    return { templateName, nameChanged, nameInput };
}
